import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import Send from '@wix/da-ds/pkg/Icons/24x24/Send';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import isValidEmailAddress from '../utils/isValidEmailAddress';
import s from './VerificationEmailInput.scss';

export interface Props {
  className?: string;
  isResendInProgress: boolean;
  isEmailChangeInProgress: boolean;
  updateFailed: boolean;
  changeEmail: (newEmail: string) => void;
  noBottomSpacing?: boolean;
}

const VerificationEmailInput: React.FC<Props> = ({
  className,
  isEmailChangeInProgress,
  isResendInProgress,
  updateFailed,
  changeEmail,
  noBottomSpacing = false,
}) => {
  const { t } = useTranslation();
  const avatar_menu_sync = useFlag('avatar_menu_sync');
  const [currentValue, setCurrentValue] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);
  const [validationFailed, setValidationFailed] = useState(false);
  const validationErrorTimeout = useRef<number | null>(null);
  const isChangeInProgress = isEmailChangeInProgress || isResendInProgress;
  const hasErrors = updateFailed || validationFailed;

  const doSubmit = () => {
    if (canSubmit && !isChangeInProgress) {
      changeEmail(currentValue);
    }
  };

  const handleInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const value = ev.target.value;
    const isValid = isValidEmailAddress(value);

    setCurrentValue(value);
    isValid !== canSubmit && setCanSubmit(isValid);

    // Avoid showing errors for 2 seconds until the user stops typing
    if (validationErrorTimeout.current) {
      window.clearTimeout(validationErrorTimeout.current);
      validationErrorTimeout.current = null;
    }
    if (isValid || !value) {
      setValidationFailed(false);
    } else {
      validationErrorTimeout.current = window.setTimeout(
        () => setValidationFailed(true),
        2000
      );
    }
  };

  const handleInputKeyPress = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (!ev.defaultPrevented && ev.key === 'Enter') {
      doSubmit();
    }
  };

  const suffix = (
    <PlainButtonOrLink
      onClick={doSubmit}
      className={cn(s['submit-btn'], !canSubmit && s['disabled'])}
    >
      {isEmailChangeInProgress ? (
        <LoadingIndicator view="spinner" className={s['loader']} />
      ) : (
        <Send />
      )}
    </PlainButtonOrLink>
  );

  return (
    <TextInput
      id="verification-email-input"
      label={t('verification.emailInput.placeholder')}
      placeholder={t('verification.emailInput.placeholder')}
      value={currentValue}
      postfix={suffix}
      validationResult={hasErrors ? 'error' : undefined}
      details={hasErrors && t('verification.emailInput.error')}
      className={cn(
        className,
        canSubmit && s['valid'],
        avatar_menu_sync && s['avatar-menu-sync']
      )}
      onChange={handleInputChange}
      onKeyPress={handleInputKeyPress}
      isLabelHidden
      noBottomSpacing={noBottomSpacing}
    />
  );
};

export default VerificationEmailInput;
